import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SVGIconModule } from '@progress/kendo-angular-icons';
import { LoadingPanelComponent } from 'src/app/shared/components/loading-panel';
import { KendoIconSets } from 'src/app/shared/constants/kendo-icon-sets';
import { CosmosCustomer } from 'src/app/shared/models/customer';
import { CustomerSummary } from 'src/app/shared/models/customer-summary';
import { SecurityService } from 'src/app/shared/security/security.service';
import { ICustomerService } from 'src/app/shared/services/customer/customer.service.interface';
import { KendoAlertService } from 'src/app/shared/services/kendo-alerts.service';
import { GridPersistService } from 'src/app/shared/services/ui/grid-persist.service';

@Component({
  selector: 'ntw-account-context-chooser',
  templateUrl: './account-context-chooser.component.html',
  styleUrls: ['./account-context-chooser.component.scss'],
  imports: [
    SVGIconModule,
    TranslateModule,
    LoadingPanelComponent
  ]
})

export class AccountContextChooserComponent implements OnInit {
  icons = KendoIconSets;
  selectedAccount: CosmosCustomer;
  contextData: CustomerSummary[] = [];
  isAccountSelectionVisible = false;
  areAccountsLoading = true;
  isChangingAccount = false;

  get canChangeContext(): boolean {
    return this.contextData.length > 1;
  }

  @ViewChild('contextToggleButton') toggleButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('contextList') contextList?: ElementRef<HTMLUListElement>;

  @HostListener('keydown', ['$event'])
  public keydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.isAccountSelectionVisible) {
      this.close();
      this.toggleButton?.nativeElement.focus();
    }
  }

  @HostListener('document:click', ['$event'])
  public documentClick(event: MouseEvent): void {
    if (!this.contains(event.target)) {
      this.toggle(false);
    }
  }

  constructor(private customerService: ICustomerService,
    private router: Router,
    public securityService: SecurityService,
    private kendoAlertService: KendoAlertService,
    private gridPersistService: GridPersistService) {
  }

  ngOnInit(): void {
    this.customerService.getCustomersByCodes(this.securityService.getCustomerAxCodes()).subscribe({
      next: customers => {
        this.contextData = customers;
        this.areAccountsLoading = false;
      },
      error: error => {
        this.kendoAlertService.showErrorAlert(error);
        this.areAccountsLoading = false;
      }
    });
    this.securityService.getCurrentAccount().subscribe({
      next: currentAccount => {
        this.selectedAccount = currentAccount;
      }
    });
  }

  public async selectCustomer(customer: CustomerSummary): Promise<void> {
    this.close();
    if (this.isChangingAccount) {
      return;
    }

    this.isChangingAccount = true;
    try {
      this.gridPersistService.ResetGridState();
      await this.router.navigate(['account', customer.AXCode]);
    } catch (error) {
      this.kendoAlertService.showErrorAlert(error);
    } finally {
      this.isChangingAccount = false;
    }
  }

  public toggle(show?: boolean): void {
    this.isAccountSelectionVisible = this.canChangeContext && !this.isChangingAccount && (show ?? !this.isAccountSelectionVisible);
  }

  public close(): void {
    this.isAccountSelectionVisible = false;
  }

  private contains(target: EventTarget | null): boolean {
    return target instanceof Node && (
      this.toggleButton?.nativeElement.contains(target) === true ||
      this.contextList?.nativeElement.contains(target) === true
    );
  }
}
